import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import styles from './Chat.module.css';

interface Props {
  draft: string;
  onDraftChange: (s: string) => void;
  onSubmit: () => void;
  recording: boolean;
  partialTranscript: string;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  disabled?: boolean;
}

const MIC_PREAMBLE_KEY = 'networker.mic.preamble.seen.v1';

export function Composer({
  draft,
  onDraftChange,
  onSubmit,
  recording,
  partialTranscript,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  disabled,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [showPreamble, setShowPreamble] = useState(false);

  // Auto-grow the textarea up to ~10 lines so the composer doesn't dominate.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [draft]);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl+Enter sends; plain Enter inserts newline (treat composer as a
    // writing surface, not an IM line).
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit();
    }
  }

  function micPress() {
    if (recording) {
      onStopRecording();
      return;
    }
    if (!localStorage.getItem(MIC_PREAMBLE_KEY)) {
      setShowPreamble(true);
      return;
    }
    onStartRecording();
  }

  function acknowledgePreamble() {
    localStorage.setItem(MIC_PREAMBLE_KEY, '1');
    setShowPreamble(false);
    onStartRecording();
  }

  return (
    <div className={styles.composerWrap}>
      {showPreamble && (
        <div className={styles.preamble}>
          <p>
            Аудио используется только для распознавания речи. Запись не сохраняется
            на сервере: после транскрипта оригинал удаляется.
          </p>
          <div className={styles.preambleRow}>
            <button onClick={() => setShowPreamble(false)} className={styles.linkBtn}>
              отменить
            </button>
            <button onClick={acknowledgePreamble} className={styles.preambleOk}>
              разрешить и говорить
            </button>
          </div>
        </div>
      )}

      <div className={styles.composer}>
        <div className={styles.composerHead}>
          <span className={styles.composerLabel}>Ваш ход</span>
          <span className={styles.composerHint}>
            {recording
              ? 'идёт запись — нажмите ещё раз, чтобы остановить'
              : 'Cmd/Ctrl + Enter — отправить'}
          </span>
        </div>

        {partialTranscript && (
          <div className={styles.partial}>« {partialTranscript} »</div>
        )}

        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => onDraftChange(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={
            recording
              ? '…слушаю'
              : 'Расскажите про встречу, или задайте вопрос про сеть…'
          }
          rows={3}
          disabled={recording}
        />

        <div className={styles.composerActions}>
          <button
            type="button"
            className={`${styles.voiceBtn} ${recording ? styles.voiceActive : ''}`}
            onClick={micPress}
            aria-pressed={recording}
          >
            {recording ? <span className={styles.voiceDot} /> : null}
            {recording ? 'остановить запись' : 'голосом'}
          </button>

          {recording && (
            <button type="button" className={styles.linkBtn} onClick={onCancelRecording}>
              отменить
            </button>
          )}

          <button
            type="button"
            className={styles.submit}
            onClick={onSubmit}
            disabled={disabled || !draft.trim()}
          >
            <span>отправить</span>
            <span className={styles.submitArrow}>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
